/**
 * Attendance claim card (iter 181, Dirk).
 *
 * Drop-in component shown inside /clock. Lets a user request that an
 * admin add a missed clock event (forgot to clock in/out, dead phone,
 * bad signal). Submits to POST /api/team/attendance/claims; admin gets
 * a notification + sees the request in the Attendance Register queue.
 *
 * The card is collapsed by default. Auto-expands when the page is
 * opened with `?claim=1` (the "File amendment" link in the reminder
 * banner navigates with this flag).
 *
 * Trust Contract: no modals. Card expands inline, validation messages
 * are inline beneath each field.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "../ui/sonner";
import api from "../../lib/api";
import { ChevronDown, ChevronUp, ClipboardCheck, Loader2 } from "lucide-react";
import DictTextarea from "../DictTextarea";

const KIND_LABELS = {
  in: "Clock in",
  out: "Clock out",
  break_start: "Break start",
  break_end: "Break end",
};

// Today's SAST date in yyyy-mm-dd — used as the default for the date input.
function _todaySast() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Johannesburg",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const obj = {};
  for (const p of parts) obj[p.type] = p.value;
  return `${obj.year}-${obj.month}-${obj.day}`;
}

export default function AttendanceClaimCard({ onSubmitted }) {
  const [params, setParams] = useSearchParams();
  const initialOpen = params.get("claim") === "1";
  const [open, setOpen] = useState(initialOpen);

  const [kind, setKind] = useState("out");
  // Iter 370 — Dirk: "Allow correction request for any date. No restriction."
  // Native date input, defaults to today in SAST. `max` = today (no
  // future) but no `min` — HR wants full historical amendments.
  const todaySast = useMemo(_todaySast, []);
  const [date, setDate] = useState(todaySast);
  const [time, setTime] = useState("16:30");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const [recent, setRecent] = useState([]);
  // [Iter 355 — 2026-02-26] Dirk: "Currently it does not update for
  // members so they don't know if admin approved it." We now poll the
  // claims list while the card is open, refresh on tab-visibility
  // change (user returns to the app), and toast a friendly "your
  // amendment was approved / rejected" the moment the status flips.
  const [seenStatuses, setSeenStatuses] = useState({});
  // Iter 398i — Dirk: "he hit File Amendment to save, and then
  // nothing happens on that screen. There's no prompt to show that
  // your amendment has been filed successfully." The submit path
  // already toasts, but toasts stack + can be missed if a manager is
  // looking at the form. Add an INLINE success banner that renders
  // above the form for 6s so the confirmation is impossible to miss.
  const [justSubmitted, setJustSubmitted] = useState(null);

  const loadRecent = async () => {
    try {
      const { data } = await api.get("/team/attendance/claims?days=14");
      const items = data?.items || [];
      // Detect approve / reject transitions so the member gets a
      // toast even if they were on this page during the decision.
      setSeenStatuses((prev) => {
        const next = { ...prev };
        for (const c of items) {
          const prior = prev[c.id];
          if (prior && prior !== c.status) {
            if (c.status === "approved") {
              toast.success(
                `Your ${(c.kind || "").replace("_", " ")} amendment was approved.`,
              );
            } else if (c.status === "rejected") {
              toast.error(
                c.admin_note
                  ? `Amendment rejected — ${c.admin_note}`
                  : "Your amendment was rejected. Speak to your manager for details.",
              );
            }
          }
          next[c.id] = c.status;
        }
        return next;
      });
      setRecent(items);
    } catch (_e) { /* noop */ }
  };

  // Poll every 20 s while the card is open. Cheap query (indexed on
  // user_id + status) — well under 50 ms server-side. Cleared on
  // unmount / card close.
  useEffect(() => {
    if (!open) return;
    loadRecent();
    const iv = setInterval(loadRecent, 20_000);
    // Refresh when the browser tab regains focus — a member who
    // Alt-Tabs back after the admin decides sees the update the
    // moment they return.
    const onVis = () => {
      if (document.visibilityState === "visible") loadRecent();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!/^\d{2}:\d{2}$/.test(time)) {
      toast.error("Time must be HH:MM (24-hour).");
      return;
    }
    if ((reason || "").trim().length < 3) {
      toast.error("Reason is required (at least 3 characters).");
      return;
    }
    setBusy(true);
    try {
      const claimed_at = `${date}T${time}:00`; // backend assumes SAST when naive
      const { data } = await api.post("/team/attendance/claims", {
        kind, claimed_at, reason: reason.trim(), note: (note || "").trim() || null,
      });
      toast.success("Amendment submitted — your manager has been notified.");
      // Iter 398i — inline success banner so the "did it save?" doubt
      // never surfaces. Auto-dismisses after 6s.
      setJustSubmitted({
        id: data?.id,
        at: claimed_at,
        kind,
      });
      setTimeout(() => {
        setJustSubmitted((cur) => (cur && cur.id === data?.id ? null : cur));
      }, 6000);
      setReason(""); setNote("");
      if (initialOpen) {
        // Strip the ?claim=1 so refresh doesn't re-open the form.
        const next = new URLSearchParams(params);
        next.delete("claim");
        setParams(next, { replace: true });
      }
      onSubmitted?.(data);
      loadRecent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit amendment.");
    } finally { setBusy(false); }
  };

  const cancelClaim = async (cid) => {
    try {
      await api.delete(`/team/attendance/claims/${cid}`);
      toast.success("Amendment cancelled.");
      loadRecent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel.");
    }
  };

  return (
    <div className="hv-card p-0 overflow-hidden" data-testid="attendance-claim-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-white/[0.03] transition"
        data-testid="attendance-claim-toggle"
      >
        <span className="inline-flex items-center gap-2 text-sm">
          <ClipboardCheck className="w-4 h-4 text-[#F59E0B]" />
          <span className="font-medium">Forgot to clock in / out?</span>
          <span className="text-[11px] text-[#a0aabf]">Request admin amendment</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-[#647087]" /> : <ChevronDown className="w-4 h-4 text-[#647087]" />}
      </button>

      {open && (
        <form onSubmit={submit} className="px-4 pb-4 pt-1 space-y-3 border-t border-white/5">
          {/* Iter 398i — inline success banner (6s auto-dismiss) so
              the moment after submitting is never ambiguous. */}
          {justSubmitted && (
            <div
              className="rounded-md border border-emerald-500/50 bg-emerald-500/10 text-emerald-200 px-3 py-2 text-sm flex items-start gap-2"
              data-testid="attendance-claim-submitted-banner"
              role="status"
              aria-live="polite"
            >
              <svg className="w-4 h-4 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z" clipRule="evenodd" />
              </svg>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">Amendment submitted for approval</div>
                <div className="text-xs opacity-90">
                  Your {(justSubmitted.kind || "").replace("_", " ")} amendment for{" "}
                  {new Date(justSubmitted.at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}
                  {" "}has been sent to your manager. You&apos;ll be notified once it&apos;s approved or rejected.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setJustSubmitted(null)}
                className="text-xs text-emerald-200/70 hover:text-white shrink-0"
                aria-label="Dismiss"
                data-testid="attendance-claim-submitted-dismiss"
              >
                ×
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Event</label>
              <select
                className="hv-input w-full mt-1"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                data-testid="attendance-claim-kind"
              >
                {Object.entries(KIND_LABELS).map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Date</label>
              <input
                type="date"
                className="hv-input w-full mt-1"
                value={date}
                max={todaySast}
                onChange={(e) => setDate(e.target.value)}
                data-testid="attendance-claim-date"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Time (SAST, 24-hour)</label>
              <input
                type="time"
                className="hv-input w-full mt-1"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="attendance-claim-time"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Reason (required)</label>
            <input
              type="text"
              className="hv-input w-full mt-1"
              value={reason}
              maxLength={500}
              placeholder="Phone died on site, manager verified arrival"
              onChange={(e) => setReason(e.target.value)}
              data-testid="attendance-claim-reason"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Note (optional)</label>
            <DictTextarea
              className="hv-input w-full mt-1 min-h-[64px]"
              value={note}
              maxLength={300}
              placeholder="Extra context for the admin"
              onChange={(e) => setNote(e.target.value)}
              data-testid="attendance-claim-note"
            />
          </div>

          <div className="flex flex-wrap gap-2 justify-end items-center">
            <div className="text-[11px] text-[#647087] mr-auto">
              Requests must be within the last 30 days. Admin will approve or reject.
            </div>
            <button
              type="submit"
              className="hv-btn hv-btn-primary text-sm disabled:opacity-50"
              disabled={busy}
              data-testid="attendance-claim-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
              Submit amendment
            </button>
          </div>

          {recent.length > 0 && (
            <div className="border-t border-white/5 pt-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-2">Recent amendments</div>
              <ul className="space-y-2 text-xs">
                {recent.slice(0, 5).map((r) => {
                  const pill = r.status === "approved"
                    ? "bg-emerald-500/15 text-emerald-300"
                    : r.status === "rejected"
                      ? "bg-red-500/15 text-red-300"
                      : r.status === "cancelled"
                        ? "bg-zinc-500/15 text-zinc-300"
                        : "bg-amber-500/15 text-amber-200";
                  return (
                    <li
                      key={r.id}
                      className="flex items-start gap-2 py-1"
                      data-testid={`attendance-claim-item-${r.id}`}
                    >
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${pill}`}>{r.status}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[#cfd6e4]">
                          {KIND_LABELS[r.kind] || r.kind} · {(r.claimed_at || "").slice(0, 16).replace("T", " ")}
                        </div>
                        <div className="text-[11px] text-[#a0aabf] truncate">{r.reason}</div>
                        {r.admin_note && (
                          <div className="text-[11px] text-[#a0aabf] italic">Admin: {r.admin_note}</div>
                        )}
                      </div>
                      {r.status === "pending" && (
                        <button
                          type="button"
                          onClick={() => cancelClaim(r.id)}
                          className="text-[11px] text-[#a0aabf] hover:text-white"
                          data-testid={`attendance-claim-cancel-${r.id}`}
                        >
                          Cancel
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </form>
      )}
    </div>
  );
}
